import './SaturationAndDecay.scss';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  FC,
  Fragment
} from 'react';
import { WidgetContainer } from '@Components/WidgetContainer';
import { ChartContainer } from '@Components/ChartContainer';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { Design, Size } from '@Components/designConstants';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { LineChart } from '@Components/Charts/LineChart';
import { exportToExcel, downloadPNG } from '@src/utils/download-utils';
import {
  capitalizeWords,
  currencyFormatter,
  MedianOrMean
} from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { CONVERSION_FORMATTER_TYPE } from '@Pages/MMM/mmm-utils';
import { CartesianGrid, Line, Tooltip, YAxis, XAxis, Area, ComposedChart } from 'recharts';
import * as R from 'ramda';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import { Brand100, getColorMap as colors, Neutral500, shadeColor } from '@src/utils/colors';
import { buildEquidistantScale, useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { SIBLING_SHADE_STEPS } from '@Pages/HROAS/utils/modelOverviewUtils';
import { MdExpandMore, MdExpandLess } from 'react-icons/md';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { SatData, DecayData } from '@blisspointmedia/bpm-types/dist/MMM';
import IncrementalityIcon from './Icons/IncrementalityIcon.svg?react';
import {
  type DecayStructure,
  hroasSaturationDataFormatter,
  transformDecayStructureToGroupedArea,
  hroasDecayDataFormatter,
  createExpandedPlatformKey,
  createPlatformKeyString,
  MODEL_TYPE_TOTAL,
  MODEL_TYPE_TOTAL_NEW,
  generateCIKeys,
  hasValidCIValues,
  dataPointHasCI,
  SaturationDataPoint,
  formatPercentRounded
} from '../utils/hroasUtils';

/* eslint-disable react-hooks/preserve-manual-memoization -- perf-critical O(n×m) saturation/decay memoization is intentional and required (see src/Pages/HROAS/CLAUDE.md); the React Compiler cannot prove it can preserve the manual useMemo/useCallback, but they must not be dropped */

interface DecayLineItem {
  dataKey: string;
  grouping?: { channel: string; platform: string; tactic: string; brand_nonbrand: string } | null;
  incrementality: boolean;
  name: string;
}

interface DecayGroupedLine {
  channel: string;
  areas: DecayLineItem[];
}

interface DecayDataFormatted {
  newD: Record<string, string | number>[];
  lines: DecayLineItem[];
  groupedLines: DecayGroupedLine[];
}

interface SaturationCurve {
  name: string;
  data: SaturationDataPoint[];
  grouping: { channel: string; platform: string; tactic: string; brand_nonbrand: string };
  incrementality: boolean;
  model_type: string;
}

interface PlatformGroup {
  platform: string;
  total: SaturationCurve | null;
  models: SaturationCurve[];
  areas: SaturationCurve[];
}

interface GroupedArea {
  channel: string;
  platforms: PlatformGroup[];
}

const MEDIAN_DOT_SIZE = 7;
const INACTIVE_COLOR = '#d3d3d3';
const X_AXIS_LABEL_SAT_HEIGHT = 16;
const X_AXIS_LABEL_SAT_OFFSET = 2;

const normalizeDecayName = (name: string): string =>
  name.replace(/_/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();

const computeShadedChildren = (
  models: SaturationCurve[],
  baseColor: string,
  parentName: string,
  resolveModelLabel: (modelType: string) => string
): Record<string, string> => {
  const result: Record<string, string> = { [parentName]: baseColor };
  const nonParent = models
    .filter(model => model.name !== parentName)
    .sort((a, b) => resolveModelLabel(a.model_type).localeCompare(resolveModelLabel(b.model_type)));
  nonParent.forEach((model, i) => {
    result[model.name] = shadeColor(baseColor, SIBLING_SHADE_STEPS[i % SIBLING_SHADE_STEPS.length]);
  });
  return result;
};

const UNSET_SCALE = { domain: undefined, ticks: undefined } as const;

const EMPTY_FORMATTED_SAT_DATA: {
  saturationCurves: SaturationCurve[];
  centralMeasures: Record<
    string,
    { mean?: number | null; median?: number | null; dataPoint: number | null }
  >;
  groupedLines: GroupedArea[];
} = {
  saturationCurves: [],
  centralMeasures: {},
  groupedLines: []
};

interface SatAndDecayProps {
  disabledPlatform: boolean | undefined;
  kpiType: string;
  satData: SatData[];
  decayData: DecayData[];
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInput: Dispatch<SetStateAction<boolean>>;
  centralMeasure: MedianOrMean;
  enabledModelTypes?: string[];
  // slot id -> display label, built in ModelResults from ModelConfig.appLabel.
  modelLabels?: Record<string, string>;
  normalizedWeights?: Map<string, number>;
  holisticDecayDataPreloaded?: DecayData[];
  decayStructurePreloaded?: DecayStructure;
  isDecayRefetching?: boolean;
}

export const SatAndDecay: FC<SatAndDecayProps> = ({
  disabledPlatform,
  kpiType,
  satData,
  decayData,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInput,
  centralMeasure,
  enabledModelTypes = [],
  modelLabels,
  normalizedWeights = new Map(),
  holisticDecayDataPreloaded,
  decayStructurePreloaded,
  isDecayRefetching = false
}) => {
  const { cid: originalCompany } = useCompanyInfo();
  let company = originalCompany;
  if (TEST_COMPANIES.includes(company)) {
    company = 'test';
  }
  const resolveModelLabel = useCallback(
    (modelType: string): string => modelLabels?.[modelType] ?? modelType,
    [modelLabels]
  );
  const hasDecayData =
    (holisticDecayDataPreloaded ?? []).length > 0 ||
    decayData.length > 0 ||
    Boolean(decayStructurePreloaded);
  const [decayDataFormatted, setDecayDataFormatted] = useState<DecayDataFormatted>({
    newD: [],
    lines: [],
    groupedLines: []
  });
  const [activeLine, setActiveLine] = useState<string | null>(null);
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const [expandedPlatforms, setExpandedPlatforms] = useState<Set<string>>(new Set());
  const [decayLegendData, setDecayLegendData] = useState<GroupedArea[]>([]);
  const [expandedDecayPlatforms, setExpandedDecayPlatforms] = useState<Set<string>>(new Set());
  const [activeDecayLine, setActiveDecayLine] = useState<string | null>(null);
  const [hoveredDecayLegend, setHoveredDecayLegend] = useState<string | null>(null);
  const companyEqualsTest = TEST_COMPANIES.includes(company);

  const formattedSatData = useMemo(() => {
    if (R.isEmpty(satData) || disabledPlatform === undefined) {
      return EMPTY_FORMATTED_SAT_DATA;
    }
    return hroasSaturationDataFormatter(
      satData,
      disabledPlatform,
      centralMeasure,
      companyEqualsTest,
      enabledModelTypes,
      normalizedWeights
    );
  }, [
    companyEqualsTest,
    disabledPlatform,
    satData,
    centralMeasure,
    enabledModelTypes,
    normalizedWeights
  ]);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    xAxisHeightModifier: x => x + X_AXIS_LABEL_SAT_HEIGHT + 5,
    yAxisWidthModifier: x => x + 8,
    reCalcDependency: formattedSatData.saturationCurves
  });

  useEffect(() => {
    let dataToUse =
      (holisticDecayDataPreloaded ?? []).length > 0 ? holisticDecayDataPreloaded! : decayData;

    if (omitDarkInputs) {
      dataToUse = dataToUse.filter(row => !row.dark_last_quarter);
    }

    if (enabledModelTypes.length > 0 && dataToUse.length > 0) {
      dataToUse = dataToUse.filter((row: DecayData) => {
        const name = row.name || '';
        const parts = name.split('__');
        if (parts.length > 1) {
          const modelType = parts[parts.length - 1];
          return enabledModelTypes.includes(modelType) || modelType === MODEL_TYPE_TOTAL_NEW;
        }
        if (row.model_type) {
          return (
            enabledModelTypes.includes(row.model_type) || row.model_type === MODEL_TYPE_TOTAL_NEW
          );
        }
        return true;
      });
    }

    if (dataToUse.length && disabledPlatform !== undefined) {
      const formatted = hroasDecayDataFormatter(
        dataToUse,
        'spend_carryover',
        disabledPlatform,
        companyEqualsTest
      );

      setDecayDataFormatted({
        newD: formatted.newD,
        lines: formatted.lines,
        groupedLines: formatted.groupedLines
      });
    } else if (dataToUse.length === 0 && disabledPlatform !== undefined) {
      setDecayDataFormatted({
        newD: [],
        lines: [],
        groupedLines: []
      });
    }
  }, [
    companyEqualsTest,
    disabledPlatform,
    decayData,
    holisticDecayDataPreloaded,
    enabledModelTypes,
    omitDarkInputs
  ]);

  // The legend is built from decayStructurePreloaded, which is computed in
  // fetchDecayCurvesFromLambda before any UI-side filtering. Prune dark (channel,
  // group) pairs here so the legend matches the filtered chart lines.
  const effectiveDecayStructure = useMemo(() => {
    if (!decayStructurePreloaded) {
      return null;
    }
    if (!omitDarkInputs) {
      return decayStructurePreloaded;
    }

    const sourceRows =
      (holisticDecayDataPreloaded ?? []).length > 0 ? holisticDecayDataPreloaded! : decayData;

    // (channel, group) keys flagged dark in the source rows. The structure builder
    // uses parseName: group is the full pre-suffix name when it has a comma, else
    // "none". Mirror that here so keys match.
    const darkKeys = new Set<string>();
    sourceRows.forEach(row => {
      if (!row.dark_last_quarter) {
        return;
      }
      const channel = row.channel ?? '';
      const fullName = (row.name ?? '').replace(/__(?:(?!__).)+$/, '');
      const group = fullName.includes(',') ? fullName : 'none';
      darkKeys.add(`${channel}|||${group}`);
    });

    if (darkKeys.size === 0) {
      return decayStructurePreloaded;
    }

    const pruned: NonNullable<DecayStructure> = {};
    Object.entries(decayStructurePreloaded).forEach(([channel, channelData]) => {
      const survivingGroups: typeof channelData.groups = {};
      Object.entries(channelData.groups).forEach(([group, groupData]) => {
        if (!darkKeys.has(`${channel}|||${group}`)) {
          survivingGroups[group] = groupData;
        }
      });
      if (Object.keys(survivingGroups).length > 0) {
        pruned[channel] = { groups: survivingGroups };
      }
    });
    return pruned;
  }, [decayStructurePreloaded, omitDarkInputs, holisticDecayDataPreloaded, decayData]);

  useEffect(() => {
    if (effectiveDecayStructure && disabledPlatform !== undefined) {
      const formattedLegendData = transformDecayStructureToGroupedArea(
        effectiveDecayStructure,
        disabledPlatform,
        enabledModelTypes,
        resolveModelLabel
      );

      setDecayLegendData(formattedLegendData);
    }
  }, [effectiveDecayStructure, disabledPlatform, enabledModelTypes, resolveModelLabel]);

  useEffect(() => {
    if (expandedPlatforms.size > 0 && activeLine !== null) {
      setActiveLine(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedPlatforms]);

  useEffect(() => {
    if (expandedDecayPlatforms.size > 0 && activeDecayLine !== null) {
      setActiveDecayLine(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedDecayPlatforms]);

  const excelDownloadSat = useCallback(() => {
    exportToExcel(satData, `${company}_saturation`);
  }, [satData, company]);

  const pngDownloadSat = useCallback(async () => {
    await downloadPNG(
      '.section4 .left .chart-container',
      `${company}_saturation`,
      '',
      [],
      true,
      true
    );
  }, [company]);

  const excelDownloadDecay = useCallback(() => {
    exportToExcel(decayData, `${company}_decay`);
  }, [decayData, company]);

  const pngDownloadDecay = useCallback(async () => {
    await downloadPNG('.section4 .right .chart-container', `${company}_decay`, '', [], true, true);
  }, [company]);

  // eslint-disable-next-line react/no-unstable-nested-components -- recharts dot renderer scoped to this chart's closures; hoisting would require threading formattedSatData + handlers
  const CustomizedDot = (props: {
    cx?: number;
    cy?: number;
    stroke?: string;
    payload?: SaturationDataPoint & { name?: string };
    saturationData: typeof formattedSatData;
  }) => {
    const { cx, cy, stroke, payload, saturationData } = props;

    if (!cx || !cy || !stroke || !payload || !payload.name) {
      return null;
    }

    const centralMeasureData = saturationData.centralMeasures?.[payload.name];
    const spendValue = payload.actualized_spend || payload.adstock_spend || 0;
    if (
      !centralMeasureData ||
      centralMeasureData.dataPoint !== spendValue ||
      centralMeasureData[centralMeasure.toLowerCase()] === null
    ) {
      return null;
    }

    return (
      <svg>
        <polygon
          points={`${cx},${cy - MEDIAN_DOT_SIZE} ${cx + MEDIAN_DOT_SIZE},${cy} ${cx},${
            cy + MEDIAN_DOT_SIZE
          } ${cx - MEDIAN_DOT_SIZE},${cy}`}
          fill={stroke}
        />
      </svg>
    );
  };

  const colorMap = useMemo(() => {
    if (!R.isEmpty(formattedSatData)) {
      return colors(formattedSatData.saturationCurves, undefined, undefined, undefined);
    }
    return undefined;
  }, [formattedSatData]);

  const decayColorMap = useMemo(() => {
    if (decayLegendData.length > 0) {
      const allDecayModels = decayLegendData.flatMap(group =>
        group.platforms.flatMap(platform => platform.models)
      );
      const decayBaseColors = colors(allDecayModels, undefined, undefined, undefined);
      const normalizedColorMap: Record<string, string> = {};
      Object.entries(decayBaseColors).forEach(([key, value]) => {
        const normalizedKey = key.replace(/__/g, ' ');
        normalizedColorMap[normalizedKey] = value as string;
      });
      return normalizedColorMap;
    }
    return {};
  }, [decayLegendData]);

  const satShadeColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (expandedPlatforms.size === 0 || !colorMap) {
      return map;
    }
    formattedSatData.groupedLines.forEach(group => {
      group.platforms.forEach(platformGroup => {
        const platformKey = `${group.channel}__${platformGroup.platform}`;
        if (!expandedPlatforms.has(platformKey)) {
          return;
        }
        const totalLine = platformGroup.models.find(
          model => model.model_type === MODEL_TYPE_TOTAL_NEW
        );
        const parent = totalLine || platformGroup.models[0];
        const baseColor = parent ? colorMap[parent.name] : undefined;
        if (!parent || !baseColor) {
          return;
        }
        Object.assign(
          map,
          computeShadedChildren(platformGroup.models, baseColor, parent.name, resolveModelLabel)
        );
      });
    });
    return map;
  }, [expandedPlatforms, colorMap, formattedSatData.groupedLines, resolveModelLabel]);

  // Keys are normalized the same way decayColorMap keys are (`__` → space) and
  // lowercased so both the legend (area.name with `__`) and the chart
  // (line.name humanized by capitalizeWords) can look up the same entry.
  const decayShadeColorMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (expandedDecayPlatforms.size === 0 || Object.keys(decayColorMap).length === 0) {
      return map;
    }
    const decayColorKeys = Object.keys(decayColorMap);
    decayLegendData.forEach(group => {
      group.platforms.forEach(platformGroup => {
        const platformKey = `${group.channel}__${platformGroup.platform}`;
        if (!expandedDecayPlatforms.has(platformKey)) {
          return;
        }
        const totalLine = platformGroup.models.find(
          model => model.model_type === MODEL_TYPE_TOTAL_NEW
        );
        const parent = totalLine || platformGroup.models[0];
        if (!parent) {
          return;
        }
        const parentNormalized = normalizeDecayName(parent.name);
        const matchingKey = decayColorKeys.find(
          key => normalizeDecayName(key) === parentNormalized
        );
        const baseColor = matchingKey ? decayColorMap[matchingKey] : undefined;
        if (!baseColor) {
          return;
        }
        const shades = computeShadedChildren(
          platformGroup.models,
          baseColor,
          parent.name,
          resolveModelLabel
        );
        Object.entries(shades).forEach(([modelName, color]) => {
          map[normalizeDecayName(modelName)] = color;
        });
      });
    });
    return map;
  }, [expandedDecayPlatforms, decayColorMap, decayLegendData, resolveModelLabel]);

  const filteredDecayLineDataKeys = useMemo(() => {
    if (!decayDataFormatted?.lines || decayLegendData.length === 0) {
      return decayDataFormatted?.lines || [];
    }

    const filtered = decayDataFormatted.lines.filter(line => {
      if (activeDecayLine !== null) {
        const normalizedActiveLine = normalizeDecayName(activeDecayLine);
        const normalizedLineName = normalizeDecayName(line.name);
        return normalizedActiveLine === normalizedLineName;
      }

      if (enabledModelTypes.length > 0) {
        const dataKey = line.dataKey || '';
        const parts = dataKey.split('__');
        if (parts.length > 1) {
          const modelType = parts[parts.length - 1];
          if (modelType !== 'Total' && !enabledModelTypes.includes(modelType)) {
            return false;
          }
        }
      }

      const { grouping } = line;

      if (!grouping) {
        return true;
      }

      const channel = grouping.channel || '';
      const platform = grouping.platform || 'Average';
      const brandNonbrand = grouping.brand_nonbrand || '';

      const platformKeyStr = createPlatformKeyString(
        platform,
        undefined,
        brandNonbrand || undefined
      );
      const platformKey = `${channel}__${platformKeyStr}`;

      const isTotalLine = line.dataKey && line.dataKey.endsWith('__Total');

      if (expandedDecayPlatforms.size === 0) {
        return isTotalLine;
      }

      if (expandedDecayPlatforms.has(platformKey)) {
        return true;
      }

      return false;
    });

    return filtered;
  }, [
    decayDataFormatted?.lines,
    decayLegendData,
    expandedDecayPlatforms,
    activeDecayLine,
    enabledModelTypes
  ]);

  const saturationCurvesWithCI = useMemo(() => {
    if (R.isEmpty(formattedSatData) || !formattedSatData?.saturationCurves) {
      return [];
    }

    return formattedSatData.saturationCurves.map(line => {
      const ciKeys = generateCIKeys(line.name);

      const dataWithCI = line.data.map(dataPoint => {
        const ciLo = dataPoint.ci_lo;
        const ciHi = dataPoint.ci_hi;
        const isValidCI = hasValidCIValues(ciLo, ciHi);

        const result: SaturationDataPoint = { ...dataPoint };

        if (isValidCI && !R.isNil(ciLo) && !R.isNil(ciHi)) {
          result[ciKeys.ciLoKey] = ciLo;
          result[ciKeys.ciBandKey] = ciHi - ciLo;
          result[ciKeys.ciHiKey] = ciHi;
          result[ciKeys.ciRangeKey] = [ciLo, ciHi];
        } else {
          result[ciKeys.ciLoKey] = null;
          result[ciKeys.ciBandKey] = null;
          result[ciKeys.ciHiKey] = null;
          result[ciKeys.ciRangeKey] = null;
        }

        return result;
      });

      return {
        ...line,
        data: dataWithCI
      };
    });
  }, [formattedSatData]);

  const { yAxisScale, xAxisScale } = useMemo(() => {
    if (activeLine === null || R.isEmpty(saturationCurvesWithCI)) {
      return { yAxisScale: UNSET_SCALE, xAxisScale: UNSET_SCALE };
    }

    const selectedLine = saturationCurvesWithCI.find(line => line.name === activeLine);
    if (!selectedLine) {
      return { yAxisScale: UNSET_SCALE, xAxisScale: UNSET_SCALE };
    }

    let yMin = Infinity;
    let yMax = -Infinity;
    let xMax = -Infinity;

    selectedLine.data.forEach((dp: SaturationDataPoint) => {
      const effect = dp.actualized_effect || dp.response || 0;
      const ciLo = dp.ci_lo;
      const ciHi = dp.ci_hi;
      const spend = dp.actualized_spend || dp.adstock_spend || 0;

      if (effect > 0) {
        yMin = Math.min(yMin, effect);
        yMax = Math.max(yMax, effect);
      }
      if (!R.isNil(ciLo) && ciLo > 0) {
        yMin = Math.min(yMin, ciLo);
      }
      if (!R.isNil(ciHi) && ciHi > 0) {
        yMax = Math.max(yMax, ciHi);
      }
      xMax = Math.max(xMax, spend);
    });

    const paddingBottom = (yMax - yMin) * 0.05;
    const paddingTop = (yMax - yMin) * 0.1;
    const domainMin = Math.max(0, yMin - paddingBottom);
    const domainMax = yMax + paddingTop;

    return {
      yAxisScale:
        yMin === Infinity || yMax === -Infinity
          ? UNSET_SCALE
          : buildEquidistantScale(domainMin, domainMax, 5),
      xAxisScale: xMax <= 0 ? UNSET_SCALE : buildEquidistantScale(0, xMax, 5)
    };
  }, [saturationCurvesWithCI, activeLine]);

  const handleLegendClick = useCallback(
    (line: { name: string }) => {
      if (activeLine === line.name) {
        setActiveLine(null);
      } else {
        setActiveLine(line.name);
      }
    },
    [activeLine]
  );

  const handleLegendEnter = useCallback(
    (line: { name: string }) => {
      if (hoveredLegend === line.name) {
        setHoveredLegend(null);
      } else {
        setHoveredLegend(line.name);
      }
    },
    [hoveredLegend]
  );

  const formatChannelPath = (grouping: {
    channel: string;
    platform: string;
    tactic: string;
    brand_nonbrand: string;
  }): string => {
    const { channel, platform, tactic, brand_nonbrand } = grouping;
    const pathParts = [channel, platform, tactic].filter(Boolean);
    const path = pathParts.join(' > ');
    return brand_nonbrand ? `${path}, ${brand_nonbrand}` : path;
  };

  const tooltip = (
    <Tooltip
      // eslint-disable-next-line react/no-unstable-nested-components -- recharts Tooltip content render-prop closing over chart state
      content={({ active, payload }) => {
        if (active && payload && payload.length) {
          const filteredPayload = payload.filter(
            (item: { name?: string | number }) =>
              !item.name || !item.name.toString().endsWith('_ci_range')
          );

          const items = R.sortBy(
            (item: {
              label: string;
              value: number;
              color?: string;
              payloadItem?: { payload?: SaturationDataPoint; name?: string };
            }) => -Math.abs(item.value),
            filteredPayload?.map(item => {
              const name = String(item.name || '');
              let modelType = MODEL_TYPE_TOTAL;
              if (name.endsWith(`__${MODEL_TYPE_TOTAL}`)) {
                modelType = MODEL_TYPE_TOTAL;
              } else {
                const lastUnderscoreIndex = name.lastIndexOf('__');
                if (lastUnderscoreIndex !== -1) {
                  modelType = name.substring(lastUnderscoreIndex + 2);
                }
              }
              const displayName = resolveModelLabel(modelType);

              return {
                label: displayName,
                value: item.value as number,
                color: item.stroke,
                payloadItem: {
                  payload: item.payload as SaturationDataPoint | undefined,
                  name: String(item.name || '')
                }
              };
            }) || []
          );

          if (kpiType === 'revenue') {
            const item = items[0];
            if (!item || (!item.value && item.value !== 0)) {
              return null;
            }
            const { label: modelLabel, payloadItem } = item;
            const payloadData: SaturationDataPoint =
              payloadItem?.payload || filteredPayload[0]?.payload || {};
            const spend = payloadData.actualized_spend || payloadData.adstock_spend || 0;
            const effect = item.value || payloadData.actualized_effect || payloadData.response || 0;
            const ciLo = payloadData.ci_lo;
            const ciHi = payloadData.ci_hi;
            const meanSpend =
              formattedSatData.centralMeasures[
                payloadItem?.name || filteredPayload[0]?.name || ''
              ]?.[centralMeasure.toLowerCase()] ?? 0;
            const roas = spend !== 0 ? (effect / spend).toFixed(2) : '0.00';

            const curve = formattedSatData.saturationCurves.find(
              c => c.name === (payloadItem?.name || filteredPayload[0]?.name)
            );
            const channelPath = curve?.grouping ? formatChannelPath(curve.grouping) : modelLabel;

            return (
              <div className='sat-chart-tooltip sat-chart-tooltip-revenue'>
                <div className='sat-chart-tooltip-header'>
                  <div className='sat-chart-tooltip-header-line sat-chart-tooltip-header-line-primary'>
                    {channelPath}
                  </div>
                  <div className='sat-chart-tooltip-header-line'>{modelLabel}</div>
                </div>
                <div className='sat-chart-tooltip-divider' />
                <div className='sat-chart-tooltip-body'>
                  <div className='sat-chart-tooltip-section'>
                    <div className='sat-chart-tooltip-row'>
                      <span className='sat-chart-tooltip-label'>Spend</span>
                      <span className='sat-chart-tooltip-value'>
                        {currencyFormatter.format(spend, 0)}
                      </span>
                    </div>
                    <div className='sat-chart-tooltip-sub-row'>
                      <span className='sat-chart-tooltip-sub-label'>Mean Spend</span>
                      <span className='sat-chart-tooltip-sub-value'>
                        {currencyFormatter.format(meanSpend, 0)}
                      </span>
                    </div>
                  </div>
                  <div className='sat-chart-tooltip-section'>
                    <div className='sat-chart-tooltip-row'>
                      <span className='sat-chart-tooltip-label'>Estimated Revenue</span>
                      <span className='sat-chart-tooltip-value'>
                        {CONVERSION_FORMATTER_TYPE.revenue.format(effect, 0)}
                      </span>
                    </div>
                    {hasValidCIValues(ciLo, ciHi) && ciLo != null && ciHi != null && (
                      <div className='sat-chart-tooltip-sub-row'>
                        <span className='sat-chart-tooltip-sub-label'>Confidence Interval</span>
                        <span className='sat-chart-tooltip-sub-value'>
                          {`${CONVERSION_FORMATTER_TYPE.revenue.format(ciLo, 0)}–${CONVERSION_FORMATTER_TYPE.revenue.format(ciHi, 0)}`}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className='sat-chart-tooltip-section'>
                    <div className='sat-chart-tooltip-row'>
                      <span className='sat-chart-tooltip-label'>ROAS</span>
                      <span className='sat-chart-tooltip-value'>{roas}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div className='sat-chart-tooltip'>
              <div className='item-list'>
                {items.map((item, i) => {
                  const { label, value, color, payloadItem } = item;
                  if (!value && value !== 0) {
                    return null;
                  }
                  const payloadData: SaturationDataPoint =
                    payloadItem?.payload || filteredPayload[0]?.payload || {};
                  const spend = payloadData.actualized_spend || payloadData.adstock_spend || 0;
                  const effect =
                    value || payloadData.actualized_effect || payloadData.response || 0;
                  const ciLo = payloadData.ci_lo;
                  const ciHi = payloadData.ci_hi;
                  const cpa = effect !== 0 ? spend / effect : 0;

                  return (
                    <div className='item-row' key={i}>
                      <div className='item-wrapper data-label'>
                        <div />
                        <div className='tooltip-label-wrapper'>
                          <div className='line' style={{ backgroundColor: color }} />
                          <div className='name'>{`${label}: `}</div>
                        </div>
                      </div>
                      <div className='item-wrapper'>
                        <div className='sat-tooltip-header-item'>Spend:</div>
                        <div className='value'>{currencyFormatter.format(spend, 0)}</div>
                      </div>
                      <div className='item-wrapper'>
                        <div className='sat-tooltip-header-item'>Mean Spend:</div>
                        <div className='value'>
                          {currencyFormatter.format(
                            formattedSatData.centralMeasures[
                              payloadItem?.name || filteredPayload[0]?.name || ''
                            ]?.[centralMeasure.toLowerCase()] || 0,
                            0
                          )}
                        </div>
                      </div>
                      <div className='item-wrapper'>
                        <div className='sat-tooltip-header-item'>{`${
                          kpiType === 'revenue' ? 'Revenue' : 'KPI Volume'
                        }:`}</div>
                        <div className='value'>
                          {CONVERSION_FORMATTER_TYPE[kpiType].format(effect, 0)}
                        </div>
                      </div>
                      {hasValidCIValues(ciLo, ciHi) && (
                        <>
                          <div className='item-wrapper'>
                            <div className='sat-tooltip-header-item'>CI Lower:</div>
                            <div className='value'>
                              {CONVERSION_FORMATTER_TYPE[kpiType].format(ciLo, 0)}
                            </div>
                          </div>
                          <div className='item-wrapper'>
                            <div className='sat-tooltip-header-item'>CI Upper:</div>
                            <div className='value'>
                              {CONVERSION_FORMATTER_TYPE[kpiType].format(ciHi, 0)}
                            </div>
                          </div>
                        </>
                      )}
                      {kpiType === 'revenue' && (
                        <div className='item-wrapper'>
                          <div className='sat-tooltip-header-item'>ROAS:</div>
                          <div className='value'>
                            {spend !== 0 ? (effect / spend).toFixed(2) : '0.00'}
                          </div>
                        </div>
                      )}
                      {kpiType === 'conversion' && (
                        <div className='item-wrapper'>
                          <div className='sat-tooltip-header-item'>CPA:</div>
                          <div className='value'>{`${
                            cpa === Infinity ? 0 : currencyFormatter.format(cpa)
                          }`}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }
        return null;
      }}
      isAnimationActive={false}
    />
  );

  const togglePlatformExpansion = useCallback((channel: string, platform: string) => {
    const platformKey = `${channel}__${platform}`;
    setExpandedPlatforms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(platformKey)) {
        newSet.delete(platformKey);
      } else {
        newSet.clear();
        newSet.add(platformKey);
      }
      return newSet;
    });
  }, []);

  const handleDecayLegendClick = useCallback(
    (line: { name: string }) => {
      if (activeDecayLine === line.name) {
        setActiveDecayLine(null);
      } else {
        setActiveDecayLine(line.name);
      }
    },
    [activeDecayLine]
  );

  const handleDecayLegendEnter = useCallback(
    (line: { name: string }) => {
      if (hoveredDecayLegend === line.name) {
        setHoveredDecayLegend(null);
      } else {
        setHoveredDecayLegend(line.name);
      }
    },
    [hoveredDecayLegend]
  );

  const toggleDecayPlatformExpansion = useCallback((channel: string, platform: string) => {
    const platformKey = `${channel}__${platform}`;
    setExpandedDecayPlatforms(prev => {
      const newSet = new Set(prev);
      if (newSet.has(platformKey)) {
        newSet.delete(platformKey);
      } else {
        newSet.clear();
        newSet.add(platformKey);
      }
      return newSet;
    });
  }, []);

  const buildLegendByGroupedArea = useCallback(
    (groupedAreas: GroupedArea[]) =>
      groupedAreas.map((group, i) => (
        <div className='legend-item' key={`satDecayLegenditem${i}`}>
          <div className='legend-value'>
            <div className='legend-header-wrapper'>
              <div className='legend-header'>
                <div className='legend-header-content'>
                  <span>{group.channel}</span>
                </div>
              </div>
            </div>
            <div className='expanded-content'>
              {group.platforms &&
                group.platforms.map((platformGroup: PlatformGroup, j: number) => {
                  const platformKeyStr = `${group.channel}__${platformGroup.platform}`;
                  const isPlatformExpanded = expandedPlatforms.has(platformKeyStr);
                  const hasAnyExpanded = expandedPlatforms.size > 0;
                  const shouldGreyOut = hasAnyExpanded && !isPlatformExpanded;

                  return (
                    <div key={`platform-${i}-${j}`}>
                      <InteractiveDiv
                        className='legend-header-wrapper clickable'
                        onClick={() =>
                          togglePlatformExpansion(group.channel, platformGroup.platform)
                        }
                      >
                        <div
                          className={`legend-header ${shouldGreyOut ? 'inactive-legend' : ''}`}
                          style={{ marginTop: j > 0 ? '6px' : '0' }}
                        >
                          <div className='legend-header-content'>
                            {!isPlatformExpanded &&
                              platformGroup.models &&
                              platformGroup.models.length > 0 &&
                              colorMap &&
                              (() => {
                                const totalLine = platformGroup.models.find(
                                  (model: SaturationCurve) =>
                                    model.model_type === MODEL_TYPE_TOTAL_NEW
                                );
                                const modelToUse = totalLine || platformGroup.models[0];
                                const hasActiveLine =
                                  activeLine === null ||
                                  platformGroup.models.some(
                                    (model: SaturationCurve) => model.name === activeLine
                                  );
                                const lineColor =
                                  shouldGreyOut || !hasActiveLine
                                    ? INACTIVE_COLOR
                                    : colorMap[modelToUse.name] || INACTIVE_COLOR;
                                return (
                                  <div
                                    className='line collapsed-line-indicator'
                                    style={{
                                      backgroundColor: lineColor
                                    }}
                                  />
                                );
                              })()}
                            <span>{platformGroup.platform}</span>
                          </div>
                          {isPlatformExpanded ? (
                            <MdExpandLess className='expand-icon' />
                          ) : (
                            <MdExpandMore className='expand-icon' />
                          )}
                        </div>
                      </InteractiveDiv>
                      {isPlatformExpanded && (
                        <div className='expanded-content' style={{ paddingLeft: '16px' }}>
                          {platformGroup.models &&
                            [...platformGroup.models]
                              .sort((a: SaturationCurve, b: SaturationCurve) => {
                                if (
                                  a.model_type === MODEL_TYPE_TOTAL_NEW &&
                                  b.model_type !== MODEL_TYPE_TOTAL_NEW
                                ) {
                                  return -1;
                                }
                                if (
                                  a.model_type !== MODEL_TYPE_TOTAL_NEW &&
                                  b.model_type === MODEL_TYPE_TOTAL_NEW
                                ) {
                                  return 1;
                                }
                                return resolveModelLabel(a.model_type).localeCompare(
                                  resolveModelLabel(b.model_type)
                                );
                              })
                              .map((area: SaturationCurve, k: number) => {
                                const isActiveArea =
                                  activeLine === null ? true : activeLine === area.name;
                                const areaBaseColor =
                                  satShadeColorMap[area.name] ?? colorMap[area.name];
                                const activeColor = isActiveArea ? areaBaseColor : INACTIVE_COLOR;
                                const hoverColor =
                                  hoveredLegend === null || hoveredLegend === area.name
                                    ? areaBaseColor
                                    : INACTIVE_COLOR;
                                const lineColor = activeLine !== null ? activeColor : hoverColor;
                                return (
                                  <InteractiveDiv
                                    key={`satDecayLegendModel${i}-${j}-${k}`}
                                    className='sub-header-wrapper'
                                    onClick={() => handleLegendClick(area)}
                                    onMouseEnter={() => handleLegendEnter(area)}
                                    onMouseLeave={() => setHoveredLegend(null)}
                                  >
                                    <div
                                      className='line'
                                      style={{
                                        backgroundColor: lineColor
                                      }}
                                    />
                                    <div className='sub-header-text-wrap'>
                                      <div
                                        className={`sub-header-1 ${
                                          isActiveArea ? '' : 'inactive-legend'
                                        }`}
                                      >
                                        <span>
                                          {resolveModelLabel(area.model_type)}
                                          {area.incrementality && (
                                            <>
                                              {' '}
                                              <IncrementalityIcon
                                                className={`incrementality-icon ${
                                                  isActiveArea || activeLine === null
                                                    ? 'active'
                                                    : 'inactive'
                                                }`}
                                              />
                                            </>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </InteractiveDiv>
                                );
                              })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )),
    [
      activeLine,
      colorMap,
      satShadeColorMap,
      handleLegendClick,
      handleLegendEnter,
      hoveredLegend,
      expandedPlatforms,
      togglePlatformExpansion,
      resolveModelLabel
    ]
  );

  const buildDecayLegendByGroupedArea = useCallback(
    (groupedAreas: GroupedArea[], decayColorMapArg: Record<string, string>) =>
      groupedAreas.map((group, i) => (
        <div className='legend-item' key={`decayLegenditem${i}`}>
          <div className='legend-value'>
            <div className='legend-header-wrapper'>
              <div className='legend-header'>
                <div className='legend-header-content'>
                  <span>{group.channel}</span>
                </div>
              </div>
            </div>
            <div className='expanded-content'>
              {group.platforms &&
                group.platforms.map((platformGroup: PlatformGroup, j: number) => {
                  const platformKeyStr = `${group.channel}__${platformGroup.platform}`;
                  const isPlatformExpanded = expandedDecayPlatforms.has(platformKeyStr);
                  const hasAnyExpanded = expandedDecayPlatforms.size > 0;
                  const shouldGreyOut = hasAnyExpanded && !isPlatformExpanded;

                  return (
                    <div key={`decay-platform-${i}-${j}`}>
                      <InteractiveDiv
                        className='legend-header-wrapper clickable'
                        onClick={() =>
                          toggleDecayPlatformExpansion(group.channel, platformGroup.platform)
                        }
                      >
                        <div
                          className={`legend-header ${shouldGreyOut ? 'inactive-legend' : ''}`}
                          style={{ marginTop: j > 0 ? '6px' : '0' }}
                        >
                          <div className='legend-header-content'>
                            {!isPlatformExpanded &&
                              platformGroup.models &&
                              platformGroup.models.length > 0 &&
                              decayColorMapArg &&
                              (() => {
                                const totalLine = platformGroup.models.find(
                                  (model: SaturationCurve) =>
                                    model.model_type === MODEL_TYPE_TOTAL_NEW
                                );
                                const modelToUse = totalLine || platformGroup.models[0];
                                const hasActiveLine =
                                  activeDecayLine === null ||
                                  platformGroup.models.some(
                                    (model: SaturationCurve) => model.name === activeDecayLine
                                  );
                                const modelNormalizedName = modelToUse
                                  ? normalizeDecayName(modelToUse.name)
                                  : null;
                                const matchingKey = modelNormalizedName
                                  ? Object.keys(decayColorMapArg).find(
                                      key => normalizeDecayName(key) === modelNormalizedName
                                    )
                                  : null;
                                const lineColor =
                                  shouldGreyOut || !hasActiveLine || !matchingKey
                                    ? INACTIVE_COLOR
                                    : decayColorMapArg[matchingKey];
                                return (
                                  <div
                                    className='line collapsed-line-indicator'
                                    style={{
                                      backgroundColor: lineColor
                                    }}
                                  />
                                );
                              })()}
                            <span>{platformGroup.platform}</span>
                          </div>
                          {isPlatformExpanded ? (
                            <MdExpandLess className='expand-icon' />
                          ) : (
                            <MdExpandMore className='expand-icon' />
                          )}
                        </div>
                      </InteractiveDiv>
                      {isPlatformExpanded && (
                        <div className='expanded-content' style={{ paddingLeft: '16px' }}>
                          {platformGroup.models &&
                            [...platformGroup.models]
                              .sort((a: SaturationCurve, b: SaturationCurve) => {
                                if (
                                  a.model_type === MODEL_TYPE_TOTAL_NEW &&
                                  b.model_type !== MODEL_TYPE_TOTAL_NEW
                                ) {
                                  return -1;
                                }
                                if (
                                  a.model_type !== MODEL_TYPE_TOTAL_NEW &&
                                  b.model_type === MODEL_TYPE_TOTAL_NEW
                                ) {
                                  return 1;
                                }
                                return resolveModelLabel(a.model_type).localeCompare(
                                  resolveModelLabel(b.model_type)
                                );
                              })
                              .map((area: SaturationCurve, k: number) => {
                                const isActiveArea =
                                  activeDecayLine === null ? true : activeDecayLine === area.name;
                                const normalizedAreaNameLower = normalizeDecayName(area.name);
                                const matchingKey = Object.keys(decayColorMapArg).find(
                                  key => normalizeDecayName(key) === normalizedAreaNameLower
                                );
                                const areaBaseColor =
                                  decayShadeColorMap[normalizedAreaNameLower] ??
                                  (matchingKey ? decayColorMapArg[matchingKey] : INACTIVE_COLOR);
                                const activeColor = isActiveArea ? areaBaseColor : INACTIVE_COLOR;
                                const hoverColor =
                                  hoveredDecayLegend === null || hoveredDecayLegend === area.name
                                    ? areaBaseColor
                                    : INACTIVE_COLOR;
                                const lineColor =
                                  activeDecayLine !== null ? activeColor : hoverColor;
                                return (
                                  <InteractiveDiv
                                    key={`decayLegendModel${i}-${j}-${k}`}
                                    className='sub-header-wrapper'
                                    onClick={() => handleDecayLegendClick(area)}
                                    onMouseEnter={() => handleDecayLegendEnter(area)}
                                    onMouseLeave={() => setHoveredDecayLegend(null)}
                                  >
                                    <div
                                      className='line'
                                      style={{
                                        backgroundColor: lineColor
                                      }}
                                    />
                                    <div className='sub-header-text-wrap'>
                                      <div
                                        className={`sub-header-1 ${
                                          isActiveArea ? '' : 'inactive-legend'
                                        }`}
                                      >
                                        <span>
                                          {area.model_type === MODEL_TYPE_TOTAL_NEW
                                            ? 'Average'
                                            : resolveModelLabel(area.model_type)}
                                          {area.incrementality && (
                                            <>
                                              {' '}
                                              <IncrementalityIcon
                                                className={`incrementality-icon ${
                                                  isActiveArea || activeDecayLine === null
                                                    ? 'active'
                                                    : 'inactive'
                                                }`}
                                              />
                                            </>
                                          )}
                                        </span>
                                      </div>
                                    </div>
                                  </InteractiveDiv>
                                );
                              })}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )),
    [
      activeDecayLine,
      decayShadeColorMap,
      handleDecayLegendClick,
      handleDecayLegendEnter,
      hoveredDecayLegend,
      expandedDecayPlatforms,
      toggleDecayPlatformExpansion,
      resolveModelLabel
    ]
  );
  /* eslint-enable react-hooks/preserve-manual-memoization */

  const xAxisLabelSat = ({
    viewBox: { x, y, width, height }
  }: {
    viewBox: { x: number; y: number; width: number; height: number };
    offset: number;
  }) => (
    <text
      x={x + width / 2}
      y={y + height + X_AXIS_LABEL_SAT_OFFSET}
      textAnchor='middle'
      className='sat-chart-x-axis-label'
    >
      Spend
    </text>
  );

  return (
    <WidgetContainer
      className='hroas-sat-decay'
      footer={
        <div className='mmm-widget-bottom-wrapper'>
          <IncrementalityIcon className='incrementality-icon-bottom' />
          <div>
            Channels marked with a green beaker use informative priors based on incrementality
            studies. Incrementality testing is strongly recommended, and we encourage you to contact
            your team to review options.
          </div>
        </div>
      }
      collapsible
      header='Cross-Model Insights'
      subHeader='The model estimation process yields business insights related to channel saturation and the lagged nature of consumer response.'
      rightActions={
        renderOmitDarkInputsToggle ? (
          <ToggleSwitch
            design={Design.NEUTRAL}
            size={Size.LG}
            label='Omit Dark Inputs'
            checked={omitDarkInputs}
            onChange={() => {
              setOmitDarkInput(current => !current);
            }}
            infoTooltipText='Dark inputs are defined as paid media features that account for <1% of spend in the previous quarter. Omitting these inputs allows for focus on active investments only.'
          />
        ) : undefined
      }
    >
      <div className='section4' style={{ display: 'flex' }}>
        <div className='left' style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <ChartContainer
            title='Saturation Curves'
            rightActions={
              <>
                <InfoTooltip size={Size.MD}>
                  Most media tactics will exhibit some form of diminishing returns, due to
                  additional investment reaching less qualified audiences and/or additional user
                  frequency having diminished impact. The model training process estimates the shape
                  of these saturation curves on a channel by channel basis. Examining these curves
                  yields insight into the scalability of each channel and the optimality of
                  incremental investments (see above section).
                </InfoTooltip>
                <DownloadDropdown
                  size={Size.SM}
                  onClickOptions={[excelDownloadSat, pngDownloadSat]}
                />
              </>
            }
          >
            {formattedSatData?.saturationCurves?.length > 0 && (
              <>
                <div className='sat-wrapper'>
                  <div className='sat-chart-title'>
                    {kpiType === 'revenue' ? 'Revenue' : 'KPI Volume'}
                  </div>
                  <div className='sat-chart-box'>
                    <AutoSizer
                      renderProp={({ width, height }) => (
                        <ComposedChart width={width ?? 0} height={height ?? 0} ref={setChartRef}>
                          <CartesianGrid
                            stroke={CARTESIAN_GRID_STROKE}
                            strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                            vertical={false}
                          />
                          <XAxis
                            axisLine={false}
                            dataKey='actualized_spend'
                            height={xAxisHeight}
                            tick={TICK_STYLE}
                            tickFormatter={val => currencyFormatter.format(val, 0)}
                            type='number'
                            label={xAxisLabelSat}
                            domain={xAxisScale.domain}
                            ticks={xAxisScale.ticks}
                          />
                          <YAxis
                            axisLine={false}
                            dataKey='actualized_effect'
                            tick={TICK_STYLE}
                            tickFormatter={val => {
                              const decimals = val > 1000000 ? 1 : 0;
                              return `${CONVERSION_FORMATTER_TYPE[kpiType].format(val, decimals)}`;
                            }}
                            tickLine={false}
                            width={yAxisWidth}
                            domain={yAxisScale.domain}
                            ticks={yAxisScale.ticks}
                          />
                          {activeLine && tooltip}

                          {(() => {
                            const hasTotalLines =
                              expandedPlatforms.size === 0 &&
                              saturationCurvesWithCI.some(
                                line => line.model_type === MODEL_TYPE_TOTAL_NEW
                              );
                            return saturationCurvesWithCI
                              .filter(line => {
                                if (line.model_type === MODEL_TYPE_TOTAL) {
                                  return false;
                                }

                                const isTotalLine = line.model_type === MODEL_TYPE_TOTAL_NEW;
                                const lineChannel = line.grouping?.channel;
                                const linePlatform = line.grouping?.platform;
                                const lineTactic = line.grouping?.tactic;
                                const lineBrandNonbrand = line.grouping?.brand_nonbrand;

                                const expandedKey = createExpandedPlatformKey(
                                  lineChannel || '',
                                  linePlatform,
                                  lineTactic,
                                  lineBrandNonbrand
                                );

                                if (activeLine !== null) {
                                  return activeLine === line.name;
                                }

                                if (expandedPlatforms.size === 0 && !hasTotalLines) {
                                  return true;
                                }

                                if (isTotalLine) {
                                  return (
                                    expandedPlatforms.size === 0 ||
                                    expandedPlatforms.has(expandedKey)
                                  );
                                }
                                return expandedPlatforms.has(expandedKey);
                              })
                              .map(line => {
                                const isActiveLine =
                                  activeLine === null ? true : activeLine === line.name;
                                const isHoveredLegend =
                                  hoveredLegend === null ? true : hoveredLegend === line.name;
                                const lineBaseColor =
                                  satShadeColorMap[line.name] ?? colorMap[line.name];
                                const activeColor = isActiveLine ? lineBaseColor : Neutral500;
                                const hoverColor = isHoveredLegend ? lineBaseColor : Neutral500;

                                const shouldShowCI =
                                  activeLine !== null &&
                                  activeLine === line.name &&
                                  line.model_type !== MODEL_TYPE_TOTAL_NEW;
                                const ciKeys = generateCIKeys(line.name);

                                const hasCIData = line.data.some((dp: SaturationDataPoint) =>
                                  dataPointHasCI(dp, ciKeys)
                                );

                                return (
                                  <Fragment key={line.name}>
                                    {shouldShowCI && hasCIData && (
                                      <Area
                                        type='linear'
                                        data={line.data}
                                        dataKey={ciKeys.ciRangeKey}
                                        fill={activeColor}
                                        fillOpacity={0.25}
                                        strokeWidth={0}
                                        stroke='transparent'
                                        dot={false}
                                        activeDot={false}
                                        isAnimationActive={false}
                                        connectNulls
                                      />
                                    )}
                                    <Line
                                      data={line.data}
                                      dataKey='actualized_effect'
                                      dot={<CustomizedDot saturationData={formattedSatData} />}
                                      isAnimationActive={false}
                                      name={line.name}
                                      stroke={activeLine ? activeColor : hoverColor}
                                      strokeWidth={3}
                                    />
                                  </Fragment>
                                );
                              });
                          })()}
                        </ComposedChart>
                      )}
                    />
                  </div>
                </div>
                <div className='right-of-chart hroas-sat-right-of-chart'>
                  <div className='legend'>
                    {(() => {
                      const legendData = R.sortBy(R.compose(R.toLower, R.prop('channel')))(
                        formattedSatData.groupedLines
                      );
                      return buildLegendByGroupedArea(legendData);
                    })()}
                  </div>
                  <div className='median-legend-label'>
                    <svg width='15' height='15' viewBox='0 0 15 15'>
                      <polygon points='7.5,0 15,7.5 7.5,15 0,7.5' fill={Brand100} />
                    </svg>
                    <div className='median-text'>
                      Mean <br /> Spend
                    </div>
                  </div>
                </div>
              </>
            )}
          </ChartContainer>
        </div>
        {hasDecayData && (
          <div className='right'>
            <ChartContainer
              rightActions={
                <>
                  <InfoTooltip size={Size.MD}>
                    Few consumers, upon being exposed to an advertisement, drop everything and
                    respond immediately; there is almost always some lag between exposure and
                    response. The model training process estimates the extent of this response lag
                    on a channel by channel basis using a technique known as Adstock. Typically
                    (though not always), we see upper-funnel tactics having longer response lags and
                    lower-funnel tactics converting more quickly. Examining these curves can help us
                    understand channel properties and set appropriate expectations for channel
                    investments.
                  </InfoTooltip>
                  <DownloadDropdown
                    size={Size.SM}
                    onClickOptions={[excelDownloadDecay, pngDownloadDecay]}
                  />
                </>
              }
              title='Spend Decay Curves'
            >
              {!R.isEmpty(decayDataFormatted) && (
                <>
                  <div className={`decay-wrapper${isDecayRefetching ? ' decay-refetching' : ''}`}>
                    <div className='decay-chart-title'>Spend Carryover</div>
                    {Object.keys(decayDataFormatted).length && (
                      <>
                        {(() => null)()}
                        <LineChart
                          customTooltipHeader={val => `Week ${val} Spend Carryover`}
                          data={decayDataFormatted?.newD}
                          colorMappings={(() => {
                            const mappings: { name: string; color: string }[] = [];
                            filteredDecayLineDataKeys.forEach(line => {
                              const normalizedLineName = normalizeDecayName(line.name);
                              const matchingKey = Object.keys(decayColorMap).find(
                                key => normalizeDecayName(key) === normalizedLineName
                              );
                              if (matchingKey && decayColorMap[matchingKey]) {
                                const normalizedHoveredLegend = hoveredDecayLegend
                                  ? normalizeDecayName(hoveredDecayLegend)
                                  : null;
                                const normalizedLineNameForComparison = normalizedLineName;
                                const isHoveredLegend =
                                  normalizedHoveredLegend === null ||
                                  normalizedHoveredLegend === normalizedLineNameForComparison;
                                const lineBaseColor =
                                  decayShadeColorMap[normalizedLineName] ??
                                  decayColorMap[matchingKey];
                                const color =
                                  activeDecayLine !== null || isHoveredLegend
                                    ? lineBaseColor
                                    : Neutral500;
                                mappings.push({
                                  name: line.name,
                                  color
                                });
                              }
                            });
                            return mappings;
                          })()}
                          lineDataKeys={filteredDecayLineDataKeys.map(line => ({
                            dataKey: line.dataKey,
                            grouping: line.grouping || undefined,
                            incrementality: line.incrementality,
                            name: line.name
                          }))}
                          groupedAreas={[]}
                          enableChannelExpansion={false}
                          showLegend={false}
                          tooltipLabelFormatter={val => {
                            if (val && val.includes('__')) {
                              const parts = val.split('__').filter(p => p !== '');
                              if (parts.length > 1) {
                                const modelType = parts[parts.length - 1];
                                if (
                                  modelType === 'Total' ||
                                  modelType === 'TOTAL' ||
                                  modelType === '__TOTAL__'
                                ) {
                                  if (expandedDecayPlatforms.size === 0) {
                                    const baseName = parts.slice(0, -1).join('__');
                                    if (baseName) {
                                      return capitalizeWords(baseName, disabledPlatform, true);
                                    }
                                  }
                                  return 'Average';
                                }
                                const displayName = resolveModelLabel(modelType);
                                if (displayName === 'Total') {
                                  return 'Average';
                                }
                                return displayName;
                              }
                            }
                            const formatted = capitalizeWords(val, disabledPlatform, true);
                            return formatted.replace(/\s+TOTAL:?\s*$/i, 'Average');
                          }}
                          tooltipFormatter={val => (val ? formatPercentRounded(val) : val)}
                          usePercentageYAxis
                          xAxisDataKey='date'
                          xAxisTickFormatter={val => `Week ${val}`}
                        />
                      </>
                    )}
                  </div>
                  {decayLegendData.length > 0 && (
                    <div className='right-of-chart'>
                      <div className='legend'>
                        {buildDecayLegendByGroupedArea(
                          R.sortBy(R.compose(R.toLower, R.prop('channel')))(decayLegendData),
                          decayColorMap
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </ChartContainer>
          </div>
        )}
      </div>
    </WidgetContainer>
  );
};
